import React from 'react';
import './RideDetailsPage.css';
import { useLanguage } from './i18n/LanguageContext';
import {
  ridePhaseOf,
  rideBadge,
  requestedVehicle,
  hasAssignedVendor,
  hasAssignedCar,
  hasAssignedDriver,
  needsOnlinePayNow,
  needsCompletedCashPay,
  RIDE_PHASE
} from './utils/rides';

const RideDetailsPage = ({ ride, onBack, onPayNow }) => {
  const { t } = useLanguage();
  if (!ride) {
    return null;
  }
  const phase = ridePhaseOf(ride);
  const requested = requestedVehicle(ride);
  const showPay = needsOnlinePayNow(ride) || needsCompletedCashPay(ride);

  const getStatusColor = (ridePhase) => {
    switch (ridePhase) {
      case RIDE_PHASE.WAITING_PAYMENT:
        return '#b45309';
      case RIDE_PHASE.WAITING_VENDOR:
      case RIDE_PHASE.VENDOR_ASSIGNED:
      case RIDE_PHASE.CAR_ASSIGNED:
        return '#3498db';
      case RIDE_PHASE.CANCELLED:
        return '#e74c3c';
      case RIDE_PHASE.COMPLETED:
        return '#95a5a6';
      default:
        return '#764CA2';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="ride-details-page">
      {/* Header */}
      <div className="ride-details-header">
        <button className="back-button" onClick={onBack}>
          <img
            src={process.env.PUBLIC_URL + '/images/back_button.svg'}
            alt="Back"
          />
        </button>
        <h1 className="ride-details-title">{t('Ride Details')}</h1>
        <div style={{ width: '40px' }}></div>
      </div>

      {/* Content */}
      <div className="ride-details-content">
        {/* Status Card */}
        <div className="status-card">
          <div
            className="status-badge"
            style={{ backgroundColor: getStatusColor(phase) }}
          >
            {t(rideBadge(ride))}
          </div>
          {(ride.bookingId || ride.id) && (
            <p className="ride-id">Ride ID: #{ride.bookingId || ride.id}</p>
          )}
        </div>

        {/* Locations Card */}
        <div className="details-card">
          <h2 className="card-title">{t('Route')}</h2>
          <div className="route-details">
            <div className="route-item">
              <div className="route-dot pickup-dot"></div>
              <div className="route-info">
                <span className="route-label">{t('Pickup')}</span>
                <span className="route-address">{ride.startLocation || 'Not specified'}</span>
                {ride.startLatitude && ride.startLongitude && (
                  <span className="route-coords">
                    {parseFloat(ride.startLatitude).toFixed(6)}, {parseFloat(ride.startLongitude).toFixed(6)}
                  </span>
                )}
              </div>
            </div>
            <div className="route-line"></div>
            <div className="route-item">
              <div className="route-dot drop-dot"></div>
              <div className="route-info">
                <span className="route-label">{t('Drop-off')}</span>
                <span className="route-address">{ride.endLocation || 'Not specified'}</span>
                {ride.endLatitude && ride.endLongitude && (
                  <span className="route-coords">
                    {parseFloat(ride.endLatitude).toFixed(6)}, {parseFloat(ride.endLongitude).toFixed(6)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Trip Details Card */}
        <div className="details-card">
          <h2 className="card-title">{t('Trip Details')}</h2>
          <div className="trip-details-grid">
            {ride.distanceKm && (
              <div className="trip-detail-item">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <div className="trip-detail-content">
                  <span className="trip-detail-label">{t('Distance')}</span>
                  <span className="trip-detail-value">{parseFloat(ride.distanceKm).toFixed(1)} km</span>
                </div>
              </div>
            )}
            {ride.minFare && ride.maxFare && (
              <div className="trip-detail-item">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6312 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6312 13.6815 18 14.5717 18 15.5C18 16.4283 17.6312 17.3185 16.9749 17.9749C16.3185 18.6312 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="trip-detail-content">
                  <span className="trip-detail-label">{t('Fare Range')}</span>
                  <span className="trip-detail-value">₹{parseFloat(ride.minFare).toFixed(0)} - ₹{parseFloat(ride.maxFare).toFixed(0)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Timestamps Card */}
        <div className="details-card">
          <h2 className="card-title">{t('Timeline')}</h2>
          <div className="timeline">
            {ride.createdAt && (
              <div className="timeline-item">
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <span className="timeline-label">{t('Created')}</span>
                  <span className="timeline-date">{formatDate(ride.createdAt)}</span>
                </div>
              </div>
            )}
            {ride.updatedAt && (
              <div className="timeline-item">
                <div className="timeline-dot"></div>
                <div className="timeline-content">
                  <span className="timeline-label">{t('Last Updated')}</span>
                  <span className="timeline-date">{formatDate(ride.updatedAt)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {(requested || ride.packageName) ? (
          <div className="details-card">
            <h2 className="card-title">{t('Requested vehicle')}</h2>
            {requested ? <p>{t('Requested: {name}', { name: requested })}</p> : null}
            {ride.packageName ? <p>{ride.packageName}</p> : null}
          </div>
        ) : null}

        {phase === RIDE_PHASE.WAITING_VENDOR ? (
          <div className="details-card">
            <h2 className="card-title">{t('Vendor Information')}</h2>
            <p>{t('Waiting for vendor')}</p>
          </div>
        ) : hasAssignedVendor(ride) ? (
          <div className="details-card">
            <h2 className="card-title">{t('Vendor Information')}</h2>
            <div className="vendor-info">
              {ride.vendorName && <p><strong>{t('Vendor Name:')}</strong> {ride.vendorName}</p>}
            </div>
          </div>
        ) : null}

        {hasAssignedCar(ride) ? (
          <div className="details-card">
            <h2 className="card-title">{t('Vehicle Information')}</h2>
            <div className="car-info">
              {ride.carName && <p><strong>{t('Car:')}</strong> {ride.carName}</p>}
              {ride.carNumber && <p><strong>{t('Car Number:')}</strong> {ride.carNumber}</p>}
            </div>
          </div>
        ) : hasAssignedVendor(ride) ? (
          <div className="details-card">
            <h2 className="card-title">{t('Vehicle Information')}</h2>
            <p>{t('Waiting for car')}</p>
          </div>
        ) : null}

        {hasAssignedCar(ride) && hasAssignedDriver(ride) ? (
          <div className="details-card">
            <h2 className="card-title">{t('Driver Information')}</h2>
            <div className="driver-info">
              {ride.driverName && <p><strong>{t('Driver Name:')}</strong> {ride.driverName}</p>}
              {ride.driverMobileNumber && <p><strong>{t('Driver phone:')}</strong> {ride.driverMobileNumber}</p>}
            </div>
          </div>
        ) : null}

        {showPay && onPayNow ? (
          <button type="button" className="pay-button" onClick={() => onPayNow(ride)}>
            {t('Pay now')}
          </button>
        ) : null}

        {ride.userName && (
          <div className="details-card">
            <h2 className="card-title">{t('Customer Information')}</h2>
            <div className="customer-info">
              <p><strong>{t('Customer Name:')}</strong> {ride.userName}</p>
              {ride.userId && <p><strong>{t('Customer ID:')}</strong> {ride.userId}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RideDetailsPage;
